import { createPathHelpers, sub } from '@routes/helpers/pathHelpers';

export const REPORTING_TABS = {
  reports: 'reports',
  textwidgets: 'textwidgets'
} as const;

export const AUTOMATIONS_TABS = {
  workflows: 'workflows',
  logs: 'logs'
} as const;

const rootPaths = {
  auth: 'auth',
  invitations: 'invitations',
  responses: 'responses',
  learningCenter: 'learning-center',
  studio: 'studio',
  accounts: 'accounts',
  campaigns: 'campaigns',
  iam: 'iam',
  approvals: 'approvals',
  workspace: 'workspace',
  datapools: 'datapools',
  insights: 'insights',
  accountSettings: 'account-settings',
  solvers: 'solvers',
  tables: 'tables',
  files: 'files',
  api: 'api',
  userProfile: 'user-profile'
} as const;

export const pathHelpers = createPathHelpers(rootPaths);

export const baseRoutes = {
  invitations: {
    root: rootPaths.invitations,
    details: ':id',
    responseDetails: ':id/responses/:responseId'
  },
  responses: {
    root: rootPaths.responses,
    details: ':id'
  },
  accountSettings: {
    root: rootPaths.accountSettings,
    settings: 'settings',
    emails: 'emails',
    profile: 'profile',
    logs: 'logs',
    branding: 'branding',
    logDetails: 'logs/:id'
  },
  studio: {
    root: rootPaths.studio,
    forms: sub('forms', {
      details: ':id',
      creator: ':id/creator'
    }),
    formGroups: sub('form-groups', {
      details: ':id'
    }),
    reporting: sub('reporting', {
      reports: REPORTING_TABS.reports,
      reportDetails: `${REPORTING_TABS.reports}/:id`,
      reportDesigner: `${REPORTING_TABS.reports}/:id/designer`,
      textwidgets: REPORTING_TABS.textwidgets,
      newTextWidget: `${REPORTING_TABS.textwidgets}/new`,
      textwidgetDetails: `${REPORTING_TABS.textwidgets}/:id`
    }),
    expressions: sub('expressions', {
      details: ':id',
      new: 'new'
    }),
    automations: sub('automations', {
      workflows: AUTOMATIONS_TABS.workflows,
      workflowDetails: `${AUTOMATIONS_TABS.workflows}/:id`,
      newWorkflow: `${AUTOMATIONS_TABS.workflows}/new`,
      logs: AUTOMATIONS_TABS.logs,
      logDetails: `${AUTOMATIONS_TABS.logs}/:id`
    }),
    taxonomies: sub('taxonomies', {
      categories: 'categories',
      tags: 'tags'
    })
  },
  iam: {
    root: rootPaths.iam,
    users: sub('users', {
      details: ':id',
      new: 'new'
    }),
    lists: sub('lists', {
      details: ':id',
      new: 'new'
    }),
    roles: sub('roles', {
      new: 'new',
      details: ':id'
    }),
    userGroups: sub('user-groups', {
      details: ':id',
      new: 'new'
    }),
    nodes: sub('nodes', {
      details: ':id',
      new: 'new'
    })
  },
  campaigns: {
    root: rootPaths.campaigns,
    list: sub('list', {
      details: ':id',
      new: 'new'
    }),
    tags: 'tags'
  },
  accounts: {
    root: rootPaths.accounts,
    entities: sub('entities', {
      new: 'new',
      details: ':id' // redirect to settings by default
    }),
    groupEntities: sub('group-entities', {
      new: 'new',
      details: ':id' // redirect to settings by default
    }),
    dataPools: sub('data-pools', {
      new: 'new',
      details: ':id'
    }),
    tags: 'tags'
  },
  approvals: {
    root: rootPaths.approvals,
    details: ':id'
  },
  api: {
    root: rootPaths.api
  },
  workspace: {
    root: rootPaths.workspace,
    history: 'history',
    details: ':id',
    historyDetails: 'history/:id',
    newResponse: ':id/responses/new',
    responseDetails: ':id/responses/:responseId'
  },
  learningCenter: {
    root: rootPaths.learningCenter,
    details: ':id',
    history: ':id/history',
    historyDetails: ':id/history/:historyId',
    newResponse: ':id/responses/new',
    responseDetails: ':id/responses/:responseId'
  },
  datapools: {
    root: rootPaths.datapools,
    details: ':id'
  },
  insights: {
    root: rootPaths.insights,
    details: ':id'
  },
  solvers: {
    root: rootPaths.solvers,
    details: ':id',
    new: 'new',
    historyDetails: ':id/history/:itemId'
  },
  tables: {
    root: rootPaths.tables,
    details: ':id',
    new: 'new',
    itemDetails: ':id/items/:itemId',
    newItem: ':id/items/new'
  },
  files: {
    root: rootPaths.files,
    details: ':id'
  }
} as const;
